import { BUTTON_GAP_MOBILE, BUTTON_GAP_DESKTOP, TRIGGER_AREA_MOBILE, TRIGGER_AREA_DESKTOP } from '@/config';
import { CONTEXTUAL_MENU_OFFSET } from '@/commons/fabricjs/FabricContextualMenuController';
import { Events } from '@/services/Events';
import { isMobile } from '@/utils/deviceDetection';
import { useConfigStore } from '@/store/config.store';
import { nextTick, onMounted, onUnmounted, ref, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import type { WFabricWorld } from '@/littleWedding/WFabricWorld';
import type { IField } from '@/typescript/interfaces/IField';
import type { Point } from 'fabric';

function useContextMenuPositionning(
    contextMenu: Ref<HTMLDivElement | null>,
    selectedField: Ref<IField | undefined>,
    fabricWorldRecto: WFabricWorld,
    fabricWorldVerso: WFabricWorld,
) {
    const absoluteLeft = ref('0px');
    const absoluteTop = ref('0px');
    const layerMenuPosition = ref({
        top: false,
        left: true,
    });
    const isLayerManagementVisible = ref(false);
    const { currentSide } = storeToRefs(useConfigStore());

    function getCurrentFabricWorld() {
        if (currentSide.value === 'recto') {
            return fabricWorldRecto;
        } else {
            return fabricWorldVerso;
        }
    }

    onMounted(() => {
        Events.on('contextual-menu-position', setMenuAbsolutePosition);
        Events.on('contextual-menu-wheel', setMenuPositionOnWheelEvent);
        Events.on('canvas-zoom-changed', updateMenuPositionAfterZoom);
        window.visualViewport?.addEventListener('resize', updateMenuPositionAfterZoom);
    });

    onUnmounted(() => {
        Events.off('contextual-menu-position', setMenuAbsolutePosition);
        Events.off('contextual-menu-wheel', setMenuPositionOnWheelEvent);
        Events.off('canvas-zoom-changed', updateMenuPositionAfterZoom);
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    });

    function updateMenuPositionAfterZoom() {
        if (!selectedField.value) return;

        const currentFabricWorld = getCurrentFabricWorld();
        const activeObject = currentFabricWorld.fabricCanvas?.getActiveObjects()[0];
        if (!activeObject) return;

        const mb = (activeObject.oCoords.mb as Point)?.clone();
        if (mb) {
            mb.y += CONTEXTUAL_MENU_OFFSET;
            setMenuAbsolutePosition(mb);
        }
    }

    function setMenuAbsolutePosition(coordinate: Point) {
        if (!contextMenu.value) return;
        let { height, width } = contextMenu.value.getBoundingClientRect();
        width = width || 52;
        height = height || 52;

        const currentFabricWorld = getCurrentFabricWorld();

        const canvasElement = currentFabricWorld.fabricCanvas?.getElement();
        if (!canvasElement) return;
        const canvasRect = canvasElement.getBoundingClientRect();
        const canvasWidth = canvasRect.width;
        const canvasHeight = canvasRect.height;

        const activeObject = currentFabricWorld.fabricCanvas?.getActiveObjects()[0];
        if (!activeObject) return;

        const top = (activeObject.oCoords.mt as Point).y;
        const bottom = (activeObject.oCoords.mb as Point).y;
        const left = (activeObject.oCoords.ml as Point).x;
        const right = (activeObject.oCoords.mr as Point).x;
        const centerY = (top + bottom) / 2;
        const centerX = coordinate.x - width / 2;

        const objectHeight = bottom - top;
        const isObjectTooTall = objectHeight > canvasHeight / 2;

        // Mobile detection and responsive adjustments
        const isMobileDetected = isMobile();
        const buttonsGap = isMobileDetected ? BUTTON_GAP_MOBILE : BUTTON_GAP_DESKTOP;
        const triggerArea = isMobileDetected ? TRIGGER_AREA_MOBILE : TRIGGER_AREA_DESKTOP;

        const isNearBottom = coordinate.y + triggerArea > canvasHeight;
        const isNearTop = coordinate.y < triggerArea;
        const isNearRight = coordinate.x > canvasWidth - width - (isMobileDetected ? 20 : triggerArea);
        const isNearLeft = coordinate.x < width + (isMobileDetected ? 20 : 0);

        if (isMobileDetected) {
            // Mobile-specific positioning: prioritize vertical positioning

            // Calculate horizontal position: ensure the menu stays within canvas bounds
            // - Math.max(10, ...): Minimum 10px from left edge to avoid cutting off
            // - Math.min(canvasWidth - width - 10, centerX): Maximum position is canvas width minus menu width minus 10px margin
            // - centerX: Preferred position (horizontally centered on the selected object)
            // Result: Menu is centered on object when possible, but clamped to stay within visible canvas area
            absoluteLeft.value = `${Math.max(10, Math.min(canvasWidth - width - 10, centerX))}px`;
            if (isNearBottom) {
                // Place above the object
                absoluteTop.value = `${Math.max(10, top - buttonsGap)}px`;
                layerMenuPosition.value.top = true;
            } else {
                // Place below the object
                absoluteTop.value = `${Math.min(canvasHeight - height - 10, bottom + buttonsGap)}px`;
                layerMenuPosition.value.top = false;
            }

            // Adjust horizontal position for mobile
            if (isNearRight) {
                absoluteLeft.value = `${canvasWidth - width - 10}px`;
                layerMenuPosition.value.left = false;
            } else if (isNearLeft) {
                absoluteLeft.value = '10px';
                layerMenuPosition.value.left = true;
            } else {
                layerMenuPosition.value.left = true;
            }
        } else {
            // Desktop positioning logic (original)
            if (isObjectTooTall) {
                if (isNearBottom) {
                    absoluteTop.value = `${top - buttonsGap * 2}px`;
                    absoluteLeft.value = `${centerX}px`;
                    layerMenuPosition.value.top = false;
                } else if (isNearTop) {
                    absoluteTop.value = `${coordinate.y}px`;
                    absoluteLeft.value = `${centerX}px`;
                    layerMenuPosition.value.top = true;
                } else if (isNearRight) {
                    absoluteLeft.value = `${left - width - buttonsGap}px`;
                    absoluteTop.value = `${centerY - height / 2}px`;
                    layerMenuPosition.value.top = false;
                    layerMenuPosition.value.left = false;
                } else if (isNearLeft) {
                    absoluteLeft.value = `${right + buttonsGap}px`;
                    absoluteTop.value = `${centerY - height / 2}px`;
                    layerMenuPosition.value.top = false;
                    layerMenuPosition.value.left = true;
                } else {
                    absoluteLeft.value = `${centerX}px`;
                    absoluteTop.value = `${coordinate.y}px`;
                    layerMenuPosition.value.top = true;
                    layerMenuPosition.value.left = true;
                }
            } else {
                if (isNearBottom) {
                    absoluteTop.value = `${top - buttonsGap * 2}px`;
                    absoluteLeft.value = `${centerX}px`;
                    layerMenuPosition.value.top = true;
                } else if (isNearRight) {
                    absoluteLeft.value = `${left - width - buttonsGap}px`;
                    absoluteTop.value = `${centerY - height / 2}px`;
                    layerMenuPosition.value.top = false;
                    layerMenuPosition.value.left = true;
                } else if (isNearLeft) {
                    absoluteLeft.value = `${right + buttonsGap}px`;
                    absoluteTop.value = `${centerY - height / 2}px`;
                    layerMenuPosition.value.top = false;
                    layerMenuPosition.value.left = false;
                } else {
                    absoluteLeft.value = `${centerX}px`;
                    absoluteTop.value = `${coordinate.y}px`;
                    layerMenuPosition.value.top = false;
                    layerMenuPosition.value.left = true;
                }
            }
        }
    }

    function setMenuPositionOnWheelEvent() {
        const currentFabricWorld = getCurrentFabricWorld();
        const isActiveObject = currentFabricWorld.fabricCanvas?.getActiveObjects();
        if (!isActiveObject || isActiveObject.length === 0) return;
        const mb = (currentFabricWorld.fabricCanvas?.getActiveObjects()[0]?.oCoords.mb as Point).clone();
        if (!mb) return;
        mb.y += CONTEXTUAL_MENU_OFFSET;
        setMenuAbsolutePosition(mb);
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    watch(selectedField, async (newField) => {
        isLayerManagementVisible.value = false;
        const currentFabricWorld = getCurrentFabricWorld();
        if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
        }
        if (newField) {
            await nextTick();
            const activeObject = currentFabricWorld.fabricCanvas?.getActiveObjects()[0];
            if (!activeObject) return;
            const mb = (activeObject.oCoords.mb as Point)?.clone();
            if (mb) {
                mb.y += CONTEXTUAL_MENU_OFFSET;
                setMenuAbsolutePosition(mb);
            }
        }
    });

    return {
        isLayerManagementVisible,
        absoluteLeft,
        absoluteTop,
        layerMenuPosition,
    };
}

export { useContextMenuPositionning };
